/**
 * Hero Collection Banner
 * Deferred JS — safe to run after DOM is ready.
 * Uses a custom element for Shopify's deferred loading pattern.
 */

class HeroCollectionBanner extends HTMLElement {
    connectedCallback() {
      this._init();
    }
  
    _init() {
      this._bindButtonHoverFallbacks();
      this._initReviewAnimation();
    }
  
    /**
     * CSS handles hover, but this adds a focus-visible style
     * for keyboard accessibility on the CTA buttons.
     */
    _bindButtonHoverFallbacks() {
      const buttons = this.querySelectorAll(
        '.hero-banner__button-primary, .hero-banner__button-secondary'
      );
  
      buttons.forEach((btn) => {
        btn.addEventListener('focus', () => btn.classList.add('is-focused'));
        btn.addEventListener('blur', () => btn.classList.remove('is-focused'));
      });
    }
  
    /**
     * Initialize the review animation cycle
     * Multiple reviews fade in from right at different vertical positions,
     * stay visible, then fade out to left sequentially
     * Multiple reviews can be visible at once at different vertical positions
     */
    _initReviewAnimation() {
      const reviewsContainer = this.querySelector('[data-reviews-container]');
      if (!reviewsContainer) return;
  
      // Only get animated reviews (exclude static fallback reviews)
      const reviews = Array.from(this.querySelectorAll('[data-review-badge]:not(.hero-banner__review-badge--static)'));
      
      // If no animated reviews (only static fallback), don't initialize animation
      if (reviews.length === 0) return;
  
      const maxVisibleReviews = 3; // Maximum number of reviews visible at once
      const minDisplayDuration = 3000; // Minimum 3 seconds visible per review
      const maxDisplayDuration = 6000; // Maximum 6 seconds visible per review
      const transitionDuration = 800; // 0.8 seconds for fade in/out
      const minDelayBetweenReviews = 800; // Minimum delay before next review appears
      const maxDelayBetweenReviews = 2500; // Maximum delay before next review appears
      
      const activeReviews = []; // Track which reviews are currently active
      const reviewTimers = new Map(); // Track timers for each review
      const activePositions = new Map(); // Track positions of active reviews {review: {bottom, right}}
      
      // Review dimensions (used for collision detection)
      const reviewWidth = 180;
      const reviewHeight = 45;
      const padding = 15; // Minimum spacing between reviews
      
      // Random number between min and max
      const randomBetween = (min, max) => Math.random() * (max - min) + min;
      
      // Check if two rectangles overlap (with padding)
      const checkOverlap = (pos1, pos2) => {
        // Convert positions to bounding boxes
        // pos.bottom and pos.right are relative to container
        // We need to check if rectangles overlap
        const box1 = {
          bottom: pos1.bottom,
          right: pos1.right,
          top: pos1.bottom + reviewHeight,
          left: pos1.right + reviewWidth
        };
        const box2 = {
          bottom: pos2.bottom,
          right: pos2.right,
          top: pos2.bottom + reviewHeight,
          left: pos2.right + reviewWidth
        };
        
        // Check for overlap with padding
        const overlap = !(
          box1.bottom > box2.top + padding ||
          box1.top + padding < box2.bottom ||
          box1.right > box2.left + padding ||
          box1.left + padding < box2.right
        );
        
        return overlap;
      };
      
      // Check if position overlaps with any active review
      const hasCollision = (position) => {
        for (const [review, existingPos] of activePositions.entries()) {
          if (checkOverlap(position, existingPos)) {
            return true;
          }
        }
        return false;
      };
      
      // The container is anchored top-left on mobile and bottom-right from tablet up.
      // Offsets always grow away from the anchored corner so badges stay on the image.
      const isAnchoredTopLeft = () => window.getComputedStyle(reviewsContainer).top !== 'auto';
      
      // Calculate random position within viewport bounds, avoiding collisions
      const getRandomPosition = (reviewElement, maxAttempts = 50) => {
        const containerRect = reviewsContainer.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const anchoredTopLeft = isAnchoredTopLeft();
        
        // Calculate max vertical offset: ensure review stays inside the viewport
        const verticalRoom = anchoredTopLeft
          ? viewportHeight - containerRect.bottom - reviewHeight - 10
          : containerRect.top - reviewHeight - 10;
        const maxBottom = Math.max(0, Math.min(verticalRoom, 200));
        
        // Calculate max horizontal offset: ensure review stays inside the viewport
        const horizontalRoom = anchoredTopLeft
          ? viewportWidth - containerRect.right - reviewWidth - 10
          : containerRect.left - reviewWidth - 10;
        const maxRight = Math.max(0, Math.min(horizontalRoom, 150));
        
        // Try to find a non-overlapping position
        let attempts = 0;
        let position;
        let hasOverlap = true;
        
        while (hasOverlap && attempts < maxAttempts) {
          // Generate random values (>= 0, within calculated bounds)
          const bottom = Math.max(0, randomBetween(0, maxBottom));
          const right = Math.max(0, randomBetween(0, maxRight));
          
          position = { bottom, right };
          hasOverlap = hasCollision(position);
          attempts++;
        }
        
        return position;
      };
      
      // Show a random review
      const showRandomReview = () => {
        // Filter out already active reviews
        const availableReviews = reviews.filter(review => !activeReviews.includes(review));
        
        // If all reviews are active or we've hit max, don't show more
        if (availableReviews.length === 0 || activeReviews.length >= maxVisibleReviews) {
          // Schedule next attempt with random delay
          const delay = randomBetween(minDelayBetweenReviews, maxDelayBetweenReviews);
          const timer = setTimeout(showRandomReview, delay);
          reviewTimers.set('next-show', timer);
          return;
        }
        
        // Pick a random available review
        const randomIndex = Math.floor(Math.random() * availableReviews.length);
        const selectedReview = availableReviews[randomIndex];
        
        // Calculate and set random position (with collision detection)
        const position = getRandomPosition(selectedReview);
        if (isAnchoredTopLeft()) {
          selectedReview.style.top = `${position.bottom}px`;
          selectedReview.style.left = `${position.right}px`;
          selectedReview.style.bottom = '';
          selectedReview.style.right = '';
        } else {
          selectedReview.style.bottom = `${position.bottom}px`;
          selectedReview.style.right = `${position.right}px`;
          selectedReview.style.top = '';
          selectedReview.style.left = '';
        }
        
        // Store position for collision detection
        activePositions.set(selectedReview, position);
        
        // Show the review
        selectedReview.style.display = 'flex';
        selectedReview.classList.remove('fade-out');
        activeReviews.push(selectedReview);
        
        // Trigger fade in from right
        requestAnimationFrame(() => {
          selectedReview.classList.add('active');
        });
        
        // Schedule fade out after random display duration
        const displayDuration = randomBetween(minDisplayDuration, maxDisplayDuration);
        const fadeOutTimer = setTimeout(() => {
          selectedReview.classList.remove('active');
          selectedReview.classList.add('fade-out');
          
          // Remove from active after transition
          setTimeout(() => {
            selectedReview.style.display = 'none';
            const index = activeReviews.indexOf(selectedReview);
            if (index > -1) {
              activeReviews.splice(index, 1);
            }
            // Remove position tracking
            activePositions.delete(selectedReview);
          }, transitionDuration);
        }, displayDuration);
        
        reviewTimers.set(selectedReview, fadeOutTimer);
        
        // Schedule next review to appear with random delay
        const delay = randomBetween(minDelayBetweenReviews, maxDelayBetweenReviews);
        const nextShowTimer = setTimeout(showRandomReview, delay);
        reviewTimers.set('next-show', nextShowTimer);
      };
  
      // Start the animation - show first review after a short random delay
      if (reviews.length > 0) {
        const initialDelay = randomBetween(500, 1500);
        setTimeout(showRandomReview, initialDelay);
      }
    }
  }
  
  // Register the custom element (safe to call multiple times — browser dedupes)
  if (!customElements.get('hero-collection-banner')) {
    customElements.define('hero-collection-banner', HeroCollectionBanner);
  }